<template>
  <!-- The foot has to clear the cart bar, which floats rather than occupying
       space: it sits 74px up on a phone so it misses the nav, and stands about
       52 tall, so the last thing on the page has to finish above roughly 126.
       Only a page with a full-width bar pays for that room. -->
  <div :class="pageClass">
    <!--
      Above the hero, not after it, and that is what finally stops it moving.

      As the first element in the flow with `sticky top-0`, it is already at its
      pinned position on the first frame — so there is no moment where it
      arrives, and no threshold to cross. The hero scrolls underneath it.

      Outside the container on purpose: the strip is a full-width band and its
      hairline runs the whole window. Its own contents are centred to the same
      width as everything else.
    -->
    <PjCategoryTabs
      v-if="hasMenu"
      ref="tabsEl"
      :active-id="activeSectionId"
      :searching="isSearching"
      :sections="sections"
      width="wide"
      @search="openSearch"
      @select="scrollToSection"
    >
      <template #search>
        <PjMenuSearch
          v-model:query="searchQuery"
          :result-count="resultCount"
          @close="closeSearch"
        />
      </template>
    </PjCategoryTabs>

    <PjContainer width="wide">
      <PjVenueHeader
        :accent="site?.accent ?? 'navy'"
        :cover-srcset="site?.coverSrcset"
        :cover-url="site?.coverUrl"
        :entry-mode="site?.entryMode"
        :has-open-tab="orderContext?.hasOpenTab"
        :is-banner="isBookingOnly"
        :address-line="site?.addressLine"
        :locality="site?.locality"
        :logo-url="site?.logoUrl"
        :name="site?.name ?? merchant?.name ?? ''"
        :ordering-mode="props.orderingMode"
        :participants="tab?.participants"
        :qr-enabled="site?.qrEnabled"
        :reopens-at="props.reopensAt"
        :tab-href="tabUrl"
        :table-name="orderContext?.tableInPlay"
        :takeaway-enabled="site?.takeawayEnabled"
      >
        <!-- A venue with no menu online still has one thing to offer: a table.
             The next free ones sit in its card, one tap each, beside where to find it.
             The slot itself is conditional, so any other venue's card ends at its name. -->
        <template
          v-if="isBookingOnly"
          #default
        >
          <div class="grid gap-8 lg:grid-cols-[minmax(0,1fr)_18rem] lg:gap-0">
            <PjTonightOffers
              class="lg:pr-8"
              heading-tag="h2"
              :is-loading="tonightOffers.isLoading.value"
              :offers="tonightOffers.offers.value"
              :today="today"
              @select-offer="bookOffer"
              @show-picker="router.visit(bookUrl ?? '')"
            />

            <section
              v-if="venueAddress"
              class="flex flex-col gap-2 border-t border-line pt-8 lg:border-l lg:border-t-0 lg:pl-8 lg:pt-0"
            >
              <h2 class="text-heading text-ink">Find us</h2>
              <p class="text-body text-muted">{{ venueAddress }}</p>
              <PjButton
                class="mt-auto self-start"
                external
                :href="directionsUrl"
                variant="soft"
              >
                Directions
              </PjButton>
            </section>
          </div>
        </template>
      </PjVenueHeader>

      <PjClosedNotice
        v-if="bannerVariant"
        class="mt-4"
        :reopens-at="props.reopensAt"
        :variant="bannerVariant"
      />

      <PjLiveOrderStrip
        v-if="site?.slug"
        class="mt-4"
        :site-slug="site.slug"
      />
    </PjContainer>

    <template v-if="hasMenu">
      <PjContainer width="wide">
        <div
          ref="menuEl"
          class="flex scroll-mt-28 flex-col gap-10 pt-8"
        >
          <div
            v-if="hasNoResults"
            class="flex flex-col items-center gap-4 py-16 text-center"
          >
            <p class="text-body text-muted">No dishes match “{{ trimmedQuery }}”.</p>
            <PjButton
              variant="soft"
              @click="clearSearch"
            >
              Clear search
            </PjButton>
          </div>
          <!-- The count the screen reader hears, on screen for everyone else. -->
          <p
            v-else-if="hasResultsLine"
            class="-mb-6 text-caption text-muted"
          >
            {{ resultsLabel }}
          </p>
          <section
            v-for="section in shownSections"
            :id="menuSectionAnchorId(section.id)"
            :key="section.id"
            :ref="el => registerSection(section.id, el)"
            class="scroll-mt-28"
          >
            <!-- Tabs jump, headings orient. Both are needed on a long menu.
                 The count is set as meta, not as part of the heading: at 800
                 weight on a line that repeats 37 times it competed with the
                 dish names underneath it. -->
            <h2 class="flex flex-wrap items-baseline gap-x-2 pb-2.5 text-heading text-ink">
              {{ section.name }}
              <span class="text-caption text-muted">
                {{ section.items.length }} {{ section.items.length === 1 ? 'dish' : 'dishes' }}
              </span>
            </h2>

            <!-- Both views column up on a wide window. A single column of rows
                 on a 2000px screen leaves two thirds of it empty and still
                 scrolls for 278 dishes; columns spend the width on content and
                 keep each row at a measure where a name stays near its price. -->
            <PjMenuGrid
              v-slot="{ item }"
              :items="section.items"
              :row-height="rowHeight"
            >
              <PjMenuLine
                v-if="isTextLayout"
                :count="countFor(item)"
                :in-venue-hint="inVenueHint"
                :item="item"
                @add="quickAdd"
                @select="handleSelect"
              />
              <PjMenuRow
                v-else
                :count="countFor(item)"
                :in-venue-hint="inVenueHint"
                :item="item"
                @add="quickAdd"
                @select="handleSelect"
              />
            </PjMenuGrid>
          </section>
        </div>
      </PjContainer>
    </template>

    <PjContainer
      v-else-if="!isBookingOnly"
      width="wide"
    >
      <PjEmpty
        :illustration="PJ_EMPTY_ILLUSTRATION.MENU"
        body="This venue hasn't put its menu up yet."
        heading="Nothing to order here yet"
      />
    </PjContainer>

    <!-- Whose product this is, said once at the foot, the same as on Places.
         It is a link to Twine rather than to another venue, so the promise that
         still holds — a venue's page carries no route to a competitor — is
         untouched by it. See ARCH-2026-011. -->
    <PjContainer width="wide">
      <PjBrandFooter class="mt-10" />
    </PjContainer>

    <PjItemSheet
      v-model:visible="sheetVisible"
      :existing-line="activeItemLine"
      :in-venue-hint="inVenueHint"
      :addon-groups="activeItemAddonGroups"
      :item="activeItem"
    />

    <PjFloatingActions :open="canOrder || !!bookUrl">
      <PjCartBar
        v-if="canOrder"
        :count="basket.itemCount"
        :has-menu="hasMenu"
        :has-open-tab="orderContext?.hasOpenTab"
        :tab-href="tabUrl"
        :tab-total="tabTotalFormatted"
        :total="basketTotal"
        @open="isCartOpen = true"
        @start="scrollToSection(sections[0]?.id)"
      />
      <PjButton
        v-if="isBookingOnly"
        block
        class="pointer-events-auto max-w-md flex-1"
        :href="bookUrl ?? undefined"
        size="md"
        variant="filled"
      >
        Book a table
      </PjButton>
      <PjButton
        v-else-if="bookUrl"
        aria-label="Reserve a table"
        class="pointer-events-auto h-btn-md w-btn-md"
        :href="bookUrl"
        icon
        variant="soft"
      >
        <PjIcon :name="PJ_ICON.CALENDAR" />
      </PjButton>
    </PjFloatingActions>

    <!-- The cart and the sign-in load once the menu is up, or the moment one
         is opened. Neither is on screen first, so neither delays the menu. -->
    <PjCartSheet
      v-if="isAfterIdle || isCartOpen"
      :context="orderContext"
      :error-message="sendError"
      :is-sending="isSending"
      :open="isCartOpen"
      :placed="placed"
      :site-slug="site?.slug ?? ''"
      @choose-mode="chooseMode"
      @close="closeCart"
      @confirm="confirmOrder"
    />

    <!-- Ordering needs a name against it, so the first tap that commits — a +,
         an add from the item sheet, choosing a table — opens this rather than
         proceeding. Browsing the menu never does. -->
    <PjSheet
      v-if="isAfterIdle || isSigningIn"
      :label="resumeName ? 'One more thing' : 'Sign in to order'"
      :open="isSigningIn"
      @close="dismissSignIn"
    >
      <div class="px-page-x">
        <PjSignIn
          :resume="resumeName"
          strap="A number is all it takes — no password. It is how the venue reaches you about the order, and how a shared table's bill adds up per person."
          @signed-in="handleSignedIn"
        />
      </div>
    </PjSheet>
  </div>
</template>

<script lang="ts" setup>
import { router, usePage } from '@inertiajs/vue3'
import { computed, defineAsyncComponent, nextTick, onMounted, onUnmounted, ref, watch, watchEffect } from 'vue'

import type { PlacedSheetOrder } from '@/domains/pjazza/components/organisms/pj-cart-sheet/PjCartSheet.types.ts'
import type { OrderMode } from '@/domains/pjazza/types/consts/OrderMode.consts.ts'
import type { OrderingMenuAddonGroup, OrderingMenuItem } from '@/domains/pjazza/types/models/OrderingMenu.types.ts'
import type { PjazzaReservationSlot } from '@/domains/pjazza/types/models/PjazzaReservation.types.ts'
import type { MenuShowProps, OrderSharedProps } from '@/domains/pjazza/types/pages.types.ts'
import type { ApiErrorShape } from '@/utils/api-error/apiError'

import PjButton from '@/domains/pjazza/components/atoms/pj-button/PjButton.vue'
import PjContainer from '@/domains/pjazza/components/atoms/pj-container/PjContainer.vue'
import { PJ_ICON } from '@/domains/pjazza/components/atoms/pj-icon/PjIcon.types.ts'
import PjIcon from '@/domains/pjazza/components/atoms/pj-icon/PjIcon.vue'
import PjBrandFooter from '@/domains/pjazza/components/molecules/pj-brand-footer/PjBrandFooter.vue'
import PjCategoryTabs from '@/domains/pjazza/components/molecules/pj-category-tabs/PjCategoryTabs.vue'
import PjClosedNotice from '@/domains/pjazza/components/molecules/pj-closed-notice/PjClosedNotice.vue'
import { PJ_EMPTY_ILLUSTRATION } from '@/domains/pjazza/components/molecules/pj-empty/PjEmpty.types.ts'
import PjEmpty from '@/domains/pjazza/components/molecules/pj-empty/PjEmpty.vue'
import PjMenuGrid from '@/domains/pjazza/components/molecules/pj-menu-grid/PjMenuGrid.vue'
import PjMenuLine from '@/domains/pjazza/components/molecules/pj-menu-line/PjMenuLine.vue'
import PjMenuRow from '@/domains/pjazza/components/molecules/pj-menu-row/PjMenuRow.vue'
import PjMenuSearch from '@/domains/pjazza/components/molecules/pj-menu-search/PjMenuSearch.vue'
import PjVenueHeader from '@/domains/pjazza/components/molecules/pj-venue-header/PjVenueHeader.vue'
import PjCartBar from '@/domains/pjazza/components/organisms/pj-cart-bar/PjCartBar.vue'
import PjFloatingActions from '@/domains/pjazza/components/organisms/pj-floating-actions/PjFloatingActions.vue'
import PjItemSheet from '@/domains/pjazza/components/organisms/pj-item-sheet/PjItemSheet.vue'
import PjLiveOrderStrip from '@/domains/pjazza/components/organisms/pj-live-order-strip/PjLiveOrderStrip.vue'
import PjSheet from '@/domains/pjazza/components/organisms/pj-sheet/PjSheet.vue'
import { useAfterIdle } from '@/domains/pjazza/composables/use-after-idle/useAfterIdle.ts'
import { useCheckoutFlow } from '@/domains/pjazza/composables/use-checkout-flow/useCheckoutFlow.ts'
import { useItemSheetRoute } from '@/domains/pjazza/composables/use-item-sheet-route/useItemSheetRoute.ts'
import { JOIN_ACTION, useOrderContext } from '@/domains/pjazza/composables/use-order-context/useOrderContext.ts'
import { useSectionScrollSpy } from '@/domains/pjazza/composables/use-section-scroll-spy/useSectionScrollSpy.ts'
import { useTabPolling } from '@/domains/pjazza/composables/use-tab-polling/useTabPolling.ts'
import { useTonightOffers } from '@/domains/pjazza/composables/use-tonight-offers/useTonightOffers.ts'
import { callPjazzaTabs } from '@/domains/pjazza/services'
import { useBasketStore } from '@/domains/pjazza/store/use-basket-store/useBasketStore.ts'
import { useLiveOrdersStore } from '@/domains/pjazza/store/use-live-orders-store/useLiveOrdersStore.ts'
import { useOrderModeStore } from '@/domains/pjazza/store/use-order-mode-store/useOrderModeStore.ts'
import { usePjazzaAuthStore } from '@/domains/pjazza/store/use-pjazza-auth-store/usePjazzaAuthStore.ts'
import { useTabStore } from '@/domains/pjazza/store/use-tab-store/useTabStore.ts'
import { useVenueChromeStore } from '@/domains/pjazza/store/use-venue-chrome-store/useVenueChromeStore.ts'
import { ADDON_GROUP_TYPE } from '@/domains/pjazza/types/consts/AddonGroupType.consts.ts'
import { HTTP_STATUS } from '@/domains/pjazza/types/consts/HttpStatus.consts.ts'
import { ORDERING_MODE } from '@/domains/pjazza/types/consts/OrderingMode.consts.ts'
import { ORDER_MODE } from '@/domains/pjazza/types/consts/OrderMode.consts.ts'
import { formatMinorAmount } from '@/domains/pjazza/utils/formatMinorAmount.ts'
import { isTabGone, TAB_GONE_MESSAGE } from '@/domains/pjazza/utils/isTabGone.ts'
import { menuSectionAnchorId } from '@/domains/pjazza/utils/menuSectionAnchor.ts'
import { indexMenu, searchMenu } from '@/domains/pjazza/utils/searchMenu.ts'
import { toDateInput } from '@/domains/pjazza/utils/toDateInput.ts'
import { MENU_LAYOUT } from '@/domains/shared/types/consts/pjazza/MenuLayout.consts.ts'
import { isApiError } from '@/utils/api-error/apiError'

const props = defineProps<MenuShowProps>()

// Off the path to the first screen, each in a chunk of its own. The sign-in
// carries libphonenumber's metadata, the largest single thing this page used to
// download before it could draw the menu.
//
// The item sheet stays with the page. It is what a tap on a dish opens, and a
// lazy chunk sharing the dish rows' helpers made Rollup fold this page into a
// shared chunk with no manifest key — which the blade's preload of the page
// relies on.
const PjCartSheet = defineAsyncComponent(
  () => import('@/domains/pjazza/components/organisms/pj-cart-sheet/PjCartSheet.vue')
)
const PjSignIn = defineAsyncComponent(() => import('@/domains/pjazza/components/organisms/pj-sign-in/PjSignIn.vue'))
// Only a bookings-only venue shows it.
const PjTonightOffers = defineAsyncComponent(
  () => import('@/domains/pjazza/components/organisms/pj-tonight-offers/PjTonightOffers.vue')
)

const page = usePage<OrderSharedProps>()

const isAfterIdle = useAfterIdle()

const merchant = computed(() => page.props.merchant)

const site = computed(() => page.props.site)

const { activeItemSlug, isSheetOpen, openItem, closeItem } = useItemSheetRoute()

const sections = computed(() => props.menu?.sections ?? [])

const hasMenu = computed(() => sections.value.length > 0)

const canOrder = computed(() => props.orderingMode === ORDERING_MODE.ORDERING)

const isBookingOnly = computed(() => props.orderingMode === ORDERING_MODE.BOOKING_ONLY)

const bannerVariant = computed(() =>
  props.orderingMode === ORDERING_MODE.CLOSED || props.orderingMode === ORDERING_MODE.MENU_ONLY
    ? props.orderingMode
    : null
)

const pageClass = computed(() => ['flex flex-col', canOrder.value || isBookingOnly.value ? 'pb-16' : 'pb-4'])

const isCartOpen = ref(false)

// Set once an order is away, which turns the cart sheet into its confirmation.
const placed = ref<PlacedSheetOrder | null>(null)

const basket = useBasketStore()

// Baskets are per-venue; arriving at a menu binds the basket to this venue
// and abandons any basket left over from a different one.
watchEffect(() => {
  if (site.value?.slug) basket.useSite(site.value.slug)
})

const basketTotal = computed(() => formatMinorAmount(basket.estimatedTotal, basket.currency))

const { activeSectionId, registerSection, scrollToSection } = useSectionScrollSpy()

const tabsEl = ref<InstanceType<typeof PjCategoryTabs> | null>(null)

const menuEl = ref<HTMLElement | null>(null)

// Our own search in place of Find on Page, which cannot reach rows the grid has not drawn. See DEC-2026-120.
const isSearching = ref(false)

const searchQuery = ref('')

// Built on the first search, not on load: folding every name costs nothing until a diner types.
const menuIndex = computed(() => indexMenu(sections.value))

const trimmedQuery = computed(() => searchQuery.value.trim())

const shownSections = computed(() =>
  isSearching.value ? searchMenu(menuIndex.value, searchQuery.value) : sections.value
)

const resultCount = computed(() => shownSections.value.reduce((count, section) => count + section.items.length, 0))

const hasNoResults = computed(() => isSearching.value && resultCount.value === 0)

const hasResultsLine = computed(() => isSearching.value && trimmedQuery.value !== '')

const resultsLabel = computed(() => (resultCount.value === 1 ? '1 dish' : `${resultCount.value} dishes`))

// Where the diner was when they opened the search, so closing it puts them back.
let scrollBeforeSearch = 0

const openSearch = (): void => {
  scrollBeforeSearch = window.scrollY
  isSearching.value = true
}

const closeSearch = async (): Promise<void> => {
  isSearching.value = false
  searchQuery.value = ''
  await nextTick()
  window.scrollTo({ top: scrollBeforeSearch })
  tabsEl.value?.focusSearch()
}

const clearSearch = (): void => {
  searchQuery.value = ''
}

// Results start at the top of the menu; a diner scrolled past it would type into an empty screen.
watch(searchQuery, () => {
  if (menuEl.value && menuEl.value.getBoundingClientRect().top < 0) menuEl.value.scrollIntoView({ block: 'start' })
})

const cssPx = (token: string): number =>
  Number.parseFloat(getComputedStyle(document.documentElement).getPropertyValue(token))

// Focus-refetch rather than a poll: four diners polling for the length of a
// meal is sixteen requests a minute for one table, and somebody picks their
// phone up to order — so it is current by the time they look at it. The tab
// page keeps the interval, because that is the screen you wait on.
const { tab, refresh: refreshTab } = useTabPolling(site.value?.slug ?? '', { poll: false })

const tabUrl = computed(() => route('pjazza.tab', { site: site.value?.slug ?? '' }))

// Only where the venue takes bookings. The flag rides the shared site props;
// while the backend does not share it yet the affordance simply stays off.
const bookUrl = computed(() =>
  site.value?.reservationsEnabled ? route('pjazza.book', { site: site.value.slug }) : null
)

const tabTotalFormatted = computed(() => tab.value?.billTotal.formatted)

// The booking page's own "Tonight?" offers, brought onto a venue that has nothing else to show.
const tonightOffers = useTonightOffers(site.value?.slug ?? '')

// The device's date, as the booking picker reads it.
const today = toDateInput(new Date())

onMounted(() => {
  if (isBookingOnly.value) void tonightOffers.fetch()
})

// Lands on the booking page's details screen with the offer's time already held.
const bookOffer = (offer: PjazzaReservationSlot): void => {
  router.visit(route('pjazza.book', { site: site.value?.slug ?? '', slot: offer.startsAt }))
}

const venueAddress = computed(() => [site.value?.addressLine, site.value?.locality].filter(Boolean).join(', '))

const directionsUrl = computed(
  () =>
    `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${site.value?.name}, ${venueAddress.value}`)}`
)

// Nothing measures the header any more. The strip is in the flow directly
// beneath it and simply stops at the top of the viewport, so the handover
// happens by itself — no reveal threshold, no bounding rect, no shared height
// constant, and no window where the tabs floated with a hole above them.

// The venue's own setting, and only that. A diner-facing toggle was built and
// removed: which way a menu reads is the venue's presentation decision, and a
// per-device override meant their setting was the one thing on the screen they
// could not rely on anybody seeing.
const isTextLayout = computed(() => site.value?.menuLayout === MENU_LAYOUT.TEXT)

const rowHeight = computed(() => cssPx(isTextLayout.value ? '--pj-menu-line' : '--pj-menu-row'))

// The count badge only reflects lines with no modifiers chosen — a dish
// configured three different ways is three lines, and showing "3" on the tile
// would imply tapping minus could undo any of them.
const countFor = (item: OrderingMenuItem): number =>
  basket.lines.filter(line => line.menuItemId === item.id).reduce((total, line) => total + line.quantity, 0)

// Map lookup keeps deep-linked (?item=slug) resolution O(1) on large menus.
const itemsBySlug = computed(() => {
  const map = new Map<string, OrderingMenuItem>()

  sections.value.forEach(section => section.items.forEach(item => map.set(item.slug, item)))

  return map
})

const activeItem = computed(() => (activeItemSlug.value ? (itemsBySlug.value.get(activeItemSlug.value) ?? null) : null))

const addonGroupsById = computed(() => new Map((props.menu?.addonGroups ?? []).map(group => [group.id, group])))

const addonGroupsOf = (item: OrderingMenuItem): OrderingMenuAddonGroup[] =>
  item.addonGroupIds.flatMap(id => addonGroupsById.value.get(id) ?? [])

const activeItemAddonGroups = computed(() => (activeItem.value ? addonGroupsOf(activeItem.value) : []))

// The one line the sheet can edit without guessing. Two lines for the same
// dish (different addons, different notes) are two distinct choices already
// made — reopening the sheet then starts a third rather than picking one to
// overwrite.
const activeItemLine = computed(() => {
  if (!activeItem.value) return null

  const lines = basket.lines.filter(line => line.menuItemId === activeItem.value?.id)

  return lines.length === 1 ? lines[0] : null
})

const sheetVisible = computed({
  get: () => isSheetOpen.value && activeItem.value !== null,
  set: value => {
    if (!value) closeItem()
  }
})

const handleSelect = (item: OrderingMenuItem): void => openItem(item.slug)

const tabStore = useTabStore()

// The nav lives in the layout and the sheets it opens live here; the store is
// how the two reach each other. See useVenueChromeStore for why.
const chrome = useVenueChromeStore()

// PjAppHeader drops its own hairline while this is set, leaving the strip's
// own border below it as the one line closing the pinned chrome. Reset on
// unmount, not just when `hasMenu` flips false — leaving this page for
// Checkout or Tab is a venue-to-venue navigation, which never fires the
// context-change reset in OrderLayout.
watchEffect(() => {
  chrome.hasCategoryStrip = hasMenu.value
})

onUnmounted(() => {
  chrome.hasCategoryStrip = false
})

const liveOrders = useLiveOrdersStore()

const auth = usePjazzaAuthStore()

// Takeaway placement, now that the cart sheet is where it happens.
const checkout = useCheckoutFlow(site.value?.slug ?? '')

const isSigningIn = ref(false)
// What to do once there is a session. Held rather than re-derived so the diner
// lands exactly where they were going instead of back at the menu.
const afterSignIn = ref<(() => void) | null>(null)
// Opens the sign-in sheet straight on the name step: a diner already signed in
// but with no name still owes one before an order carries their table.
const resumeName = ref(false)

/**
 * Ordering starts with knowing who is ordering.
 *
 * Returns true when the caller may proceed. When it returns false a sign-in has
 * opened over the menu and the intent is parked — so every entry point reads the
 * same way: ask, and either carry on or get replayed.
 *
 * Deliberately not on arrival: the menu stays public and readable without an
 * account, which is what makes a venue's page worth linking to. The gate sits on
 * the first thing that commits you.
 */
const requireSignIn = (next: () => void): boolean => {
  if (auth.isSignedIn) return true

  afterSignIn.value = next
  isSigningIn.value = true

  return false
}

/**
 * A placed order needs a name against it, or the venue calls out "Guest". A new
 * sign-in can no longer skip the name, so this only ever catches a session that
 * predates that rule: it sends the diner to the name step and replays the
 * placement once the name is on file. Same park-and-replay shape as the sign-in
 * gate, so both order paths read identically.
 */
const ensureName = (next: () => void): boolean => {
  if (auth.name) return true

  afterSignIn.value = next
  resumeName.value = auth.isSignedIn
  isSigningIn.value = true

  return false
}

/**
 * The store refuses an unsigned add and parks the selection; this is what turns
 * that into a sign-in. Watching the parked selection rather than gating each
 * button means the quick +, the item sheet and anything added later are all
 * covered by one line — including call sites that do not exist yet.
 */
watch(
  () => basket.pendingSelection,
  parked => {
    if (parked) isSigningIn.value = true
  }
)

watch(isSigningIn, signingIn => {
  if (signingIn && isCartOpen.value) {
    isCartOpen.value = false
  }
})

const handleSignedIn = (): void => {
  isSigningIn.value = false
  resumeName.value = false

  const next = afterSignIn.value

  afterSignIn.value = null

  // A blocked add is parked in the basket store, so replaying it belongs to the
  // store rather than to whichever button was pressed.
  if (basket.pendingSelection && basket.addPending()) {
    // The item sheet keeps itself open when its add is refused, so that the
    // selection is still on screen behind the sign-in. Once the line has landed
    // it has nothing left to show.
    closeItem()
  }

  next?.()
}

const dismissSignIn = (): void => {
  isSigningIn.value = false
  resumeName.value = false
  afterSignIn.value = null
  basket.cancelPending()
}

const isSending = ref(false)
// Whether the picker was opened by the Pay button or by the hero — the first
// sends the round on choosing, the second only remembers the table.
const isSendingAfterPick = ref(false)
const sendError = ref<string | null>(null)
const pendingNote = ref('')

// The one resolver, shared with the shell's header. What the order is, and what
// is still needed to send it, is a single question with a single answer.
const orderContext = useOrderContext()

// Shared via a store so the header control and the cart toggle set the one same
// choice; the resolver decides when that choice actually applies.
const orderMode = useOrderModeStore()

/** What a dish sold in the venue only says in place of its add button (DEC-2026-083). */
const inVenueHint = computed(() =>
  orderContext.value?.mode === ORDER_MODE.DINE_IN ? 'Ask staff to order' : 'Available in the restaurant only'
)

/**
 * A waiter-session venue has no table to switch the mode to — dine-in there
 * is a printed code, not a state modeFor() can just declare — so choosing it
 * opens the scanner instead of leaving the store's choice to be silently
 * discarded until the diner is holding a table it can apply to.
 *
 * Closes the cart first when this fires from its own toggle: PjSheet stacks
 * whatever is open rather than replacing it, so leaving the cart open behind
 * the scanner showed both at once instead of one sheet handing off to the
 * next. The basket survives being closed — only `open` changes.
 */
const chooseMode = (next: OrderMode): void => {
  orderMode.choose(next)

  if (next === ORDER_MODE.DINE_IN && orderContext.value?.join === JOIN_ACTION.SCAN) {
    closeCart()
    chrome.openScan()
  }
}

/**
 * Same button, four destinations, all of them read off the one context.
 *
 * An open tab appends the round to the bill; a table already named mints the
 * session and sends; a picker-mode venue with no table asks which one; and a
 * printed-code venue sends them to the scanner, because naming a table there is
 * a 404 the diner would meet as a silent takeaway.
 */
const confirmOrder = async (note: string): Promise<void> => {
  const slug = site.value?.slug
  const ctx = orderContext.value

  if (!slug || !ctx) return

  if (ctx.mode === ORDER_MODE.TAKEAWAY) {
    await placeTakeaway(note)

    return
  }

  if (ctx.hasOpenTab) {
    await sendRound(tabStore.tokenFor(slug), null, note)

    return
  }

  const chosen = tabStore.pendingTableFor(slug)

  // Already told us where they are sitting — asking again at the till would be
  // asking a question they answered in the header.
  if (chosen) {
    await sendRound(null, chosen.tableId, note)

    return
  }

  if (ctx.join === JOIN_ACTION.SCAN) {
    closeCart()
    chrome.openScan()

    return
  }

  // Held because the picker replaces the sheet the note was typed into.
  pendingNote.value = note
  isSendingAfterPick.value = true
  closeCart()
  openTablePicker()
}

/**
 * Takeaway is placed from the cart sheet, not from a screen of its own.
 *
 * Checkout was a page, and by the time the sign-in gate moved to add-to-cart
 * there was almost nothing left on it: the phone step is unreachable for anyone
 * who got this far, and the name and email it asked for are already on the
 * account. What remained was a note and a button — both of which the sheet
 * already has — so the page was a navigation charged for nothing.
 *
 * The name still travels, from the account rather than from a field, because the
 * venue calls it out at the counter.
 */
// Dismissing the confirmation is also what clears it, so the next time the cart
// opens it is a cart again rather than the last order's receipt.
const closeCart = (): void => {
  isCartOpen.value = false
  placed.value = null
}

const placeTakeaway = async (note: string): Promise<void> => {
  // A name first, if this session still has none — then the placement replays.
  if (!ensureName(() => placeTakeaway(note))) return

  checkout.notes.value = note
  checkout.name.value = auth.name ?? ''

  // Read now: placing empties the basket, and the confirmation still has to be
  // able to say what it came to.
  const total = basketTotal.value

  isSending.value = true
  sendError.value = null

  const result = await checkout.placeNow()

  isSending.value = false

  // The composable already turned the failure into something sayable; the sheet
  // is what says it, so it has to come back out here.
  if (!result) {
    sendError.value = checkout.errorMessage.value

    return
  }

  /*
   * A venue that takes the money first answers with its provider's payment
   * page, never an order — so the browser has to leave for it.
   *
   * Checkout was a page when this was written, and that page still does this;
   * the sheet did not, so a SumUp venue showed the "sent" confirmation over the
   * menu instead. Nothing had been sent: no order exists until the money lands,
   * "Track your order" was an Inertia link to another origin and so did nothing,
   * and the kitchen never heard about any of it.
   */
  if (result.isPayment) {
    window.location.assign(result.statusUrl)

    return
  }

  liveOrders.increment()

  // Stays open, and becomes the confirmation. Navigating away to say "sent"
  // threw away the menu the order was placed from, and the way back out of the
  // status screen was to load it again.
  placed.value = { total, statusUrl: result.statusUrl, lines: result.lines, order: result.order }
}

/**
 * Opened from the hero (or the app header), where the diner is answering "where are you sitting"
 * and nothing is being ordered. Clears the flag rather than trusting it: a
 * picker dismissed from the Pay button would otherwise leave it set, and the
 * next choice from the hero would silently send an order.
 */
const openTablePicker = (): void => {
  if (!requireSignIn(() => chrome.openPicker())) return

  chrome.openPicker()
}

watch(
  () => tabStore.pendingTable,
  async newTable => {
    if (isSendingAfterPick.value && newTable?.tableId) {
      isSendingAfterPick.value = false
      await sendRound(null, newTable.tableId, pendingNote.value)
    }
  }
)

// The table picker button was moved to the layout header. When clicked there,
// it sets chrome.isPickerOpen. We intercept that here to route it through the
// gated flow instead of bypassing the sign-in check.
watch(
  () => chrome.isPickerOpen,
  isOpen => {
    if (isOpen) {
      chrome.isPickerOpen = false
      openTablePicker()
    }
  }
)

const sendRound = async (sessionToken: string | null, tableId: string | null, note: string): Promise<void> => {
  const slug = site.value?.slug

  if (!slug || (!sessionToken && !tableId)) return

  // A round lands on a shared bill, so the venue has to be able to say whose it
  // is — a name first if this session still has none, then the send replays.
  if (!ensureName(() => sendRound(sessionToken, tableId, note))) return

  isSending.value = true
  sendError.value = null

  try {
    const response = await callPjazzaTabs().placeRound({
      siteSlug: slug,
      sessionToken,
      tableId,
      items: basket.toPayload(),
      notes: note.trim() || null,
      name: auth.name,
      idempotencyKey: basket.idempotencyKey
    })

    const round = response.data.value?.data

    if (!round) throw new Error('No order returned')

    // Picker mode mints the session on this first round, so the device has to
    // keep it — from here on both entry modes are the same flow.
    if (round.sessionToken) {
      tabStore.join({
        siteSlug: slug,
        sessionToken: round.sessionToken,
        tabId: round.tabId,
        tableName: round.tableName
      })
    }

    basket.clear()
    liveOrders.increment()
    isCartOpen.value = false

    // The strip in the hero is now a round out of date — the diner who just
    // ordered is a participant they should see themselves in.
    void refreshTab()

    router.visit(route('pjazza.tab', { site: slug }))
  } catch (error) {
    const err: ApiErrorShape = isApiError(error) ? error : {}
    const isUnauth = err.status === HTTP_STATUS.UNAUTHORIZED || err.data?.message === 'Unauthenticated.'

    if (isUnauth) {
      auth.signOut()
      sendError.value = 'Your session has expired. Please sign in again.'
      isCartOpen.value = true
    } else if (isTabGone(error)) {
      // The table was settled while this basket was being assembled. Forgetting
      // it drops the chip from the hero, and the basket is kept: the items are
      // still what they wanted, they just need a live table to send them to.
      tabStore.leave()
      sendError.value = TAB_GONE_MESSAGE
      isCartOpen.value = true
    } else if (err.status === HTTP_STATUS.TOO_MANY_REQUESTS) {
      // Tripped the placement throttle by retrying quickly. The round is safe;
      // it just needs a moment rather than the generic "try again" that reads
      // as a failure and pulls another immediate tap.
      sendError.value = 'One moment — give it a few seconds, then try again.'
      isCartOpen.value = true
    } else {
      sendError.value =
        err.data?.message ?? "That didn't make it onto the table order. Try again, or ask a member of staff."
    }
  } finally {
    isSending.value = false
  }
}

/**
 * The + on a tile adds the dish outright, but only when there is nothing to
 * choose. A dish with a required modifier group cannot be added blind, so its
 * + opens the sheet instead — the alternative is an order the kitchen cannot
 * make.
 */
const quickAdd = (item: OrderingMenuItem): void => {
  const addonGroups = addonGroupsOf(item)
  const needsChoice = addonGroups.some(group => (group.min ?? 0) > 0 || group.type === ADDON_GROUP_TYPE.SINGLE)

  if (needsChoice) {
    openItem(item.slug)

    return
  }

  basket.add({ item, addonGroups, addons: [], notes: null, quantity: 1 })
}
</script>
